import { map, result, compose } from 'underscore';
import Promise from 'bluebird';
import { getODataErrors } from '@bingads-webui-campaign/api-errors';

/* eslint-disable no-param-reassign */
// Maps single OData error to another error code and remaps the property to the
// specified in `propertyName` property. The error code map is Code -> Code.
const mapErrorCodeForProperty = (errorCodeMap, propertyName) => (error) => {
  error.Code = result(errorCodeMap, error.Code, error.Code);
  error.Property = propertyName;
  return error;
};

// Maps a single OData error to another error code based on property specific
// error codes mappings. The error code map is Property -> Code -> Code.
const mapErrorCode = errorCodeMap => (error) => {
  const propertySpecificMap = result(errorCodeMap, error.Property, {});

  error.Code = result(propertySpecificMap, error.Code, error.Code);
  return error;
};

// Maps a single OData error to another property. The error to property map is
// Code -> Property.
// This is usefull when MT doesn't include property information, but the error code
// is applicable to a single property.
const mapErrorProperty = errorCodePropertyMap => (error) => {
  error.Property = result(errorCodePropertyMap, error.Code, error.Property);
  return error;
};
/* eslint-enable no-param-reassign */

/**
 * Used on OData error response to a direct OData request promise, based on a single
 * model property. Remaps all errors to the specified in `propertyName` property,
 * first remapping properties based on error codes specified in the optional `errorCodePropertyMap`
 * @param {Object} i18n - the i18n instance
 * @param {Object} logger - the optional logger instance
 * @param {ErrorCodeMap} errorCodeMap - the error code to erro code map
 * @param {*} propertyName - the property to set all errors to
 * @returns {mapODataErrorsForPropertyFunc} - the mapping function
 */
export const mapODataErrorsForProperty =
  (i18n, logger, errorCodeMap = {}, propertyName) => (response) => {
    if (response.errors) {
      return getODataErrors(
        map(
          response.errors,
          mapErrorCodeForProperty(errorCodeMap, propertyName)
        ),
        i18n,
        logger
      );
    }

    return response;
  };

/**
 * Used in legacy repository. Used on OData error response. Remaps all errors to the
 * specified in `propertyName` property, first remapping properties based on error
 * codes specified in the optional `errorCodePropertyMap`
 * @param {Object} i18n - the i18n instance
 * @param {Object} logger - the optional logger instance
 * @param {ErrorCodeMap} errorCodeMap - the error code to erro code map
 * @param {ErrorCodePropertyMap} errorCodePropertyMap - the error code to property map
 * @returns {mapODataErrorsFunc} - the mapping function
 */
export const mapODataErrors =
  (i18n, logger, errorCodeMap = {}, errorCodePropertyMap = {}) => (response) => {
    if (response.errors) {
      return getODataErrors(
        map(
          response.errors,
          compose(
            mapErrorCode(errorCodeMap),
            mapErrorProperty(errorCodePropertyMap)
          )
        ),
        i18n,
        logger
      );
    }

    return response;
  };

const throwReturn = func => (arg0) => { throw func(arg0); };

// Used to wrap and process direct OData request promise based on single model property
export const withAsyncOdataErrorForProperty =
  (i18n, logger, errorCodeMap = {}, propertyName, promise) =>
    Promise
      .resolve(promise)
      .catch(throwReturn(mapODataErrorsForProperty(i18n, logger, errorCodeMap, propertyName)));
